import React from "react";

interface IncomingMailsProps {
  title?: string;
  subtitle?: string;
  width?: number | string;
  height?: number | string;
}

const IncomingMails: React.FC<IncomingMailsProps> = ({
  title = "No incoming mail",
  subtitle = "New messages will appear here",
  width = "100%",
  height = "auto",
}) => {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0 }}>
      <svg
        width={width}
        height={height}
        viewBox="0 0 680 320"
        role="img"
        xmlns="http://www.w3.org/2000/svg"
        aria-label={title}
      >
        <title>{title}</title>
        <desc>Illustration of an incoming mail tray with envelopes arriving from above</desc>

        {/* Tray base */}
        <rect x="180" y="210" width="320" height="18" rx="5" fill="#c4baf0" />
        <rect x="192" y="175" width="296" height="44" rx="6" fill="#e8e4f8" stroke="#c4baf0" strokeWidth="1.5" />
        <rect x="180" y="218" width="14" height="60" rx="4" fill="#d4cef5" stroke="#c4baf0" strokeWidth="1" />
        <rect x="486" y="218" width="14" height="60" rx="4" fill="#d4cef5" stroke="#c4baf0" strokeWidth="1" />
        <rect x="192" y="268" width="296" height="12" rx="4" fill="#e0dbf5" stroke="#c4baf0" strokeWidth="1" />

        {/* Envelope in tray */}
        <rect x="212" y="182" width="256" height="30" rx="4" fill="#ffffff" stroke="#c4baf0" strokeWidth="1" />
        <polyline points="212,182 340,208 468,182" fill="none" stroke="#c4baf0" strokeWidth="1.2" />
        <line x1="212" y1="212" x2="290" y2="196" stroke="#c4baf0" strokeWidth="0.8" opacity="0.5" />
        <line x1="468" y1="212" x2="390" y2="196" stroke="#c4baf0" strokeWidth="0.8" opacity="0.5" />

        {/* Floating envelope 1 - mid */}
        <g transform="translate(260, 105)">
          <rect x="0" y="0" width="160" height="52" rx="5" fill="#f0edfb" stroke="#b8b0e8" strokeWidth="1.2" />
          <polyline points="0,0 80,32 160,0" fill="none" stroke="#b8b0e8" strokeWidth="1.1" />
          <line x1="0" y1="52" x2="56" y2="30" stroke="#c4baf0" strokeWidth="0.8" opacity="0.5" />
          <line x1="160" y1="52" x2="104" y2="30" stroke="#c4baf0" strokeWidth="0.8" opacity="0.5" />
          <line x1="22" y1="22" x2="75" y2="22" stroke="#c4baf0" strokeWidth="2" strokeLinecap="round" opacity="0.4" />
          <line x1="22" y1="33" x2="60" y2="33" stroke="#c4baf0" strokeWidth="2" strokeLinecap="round" opacity="0.25" />
        </g>

        {/* Floating envelope 2 - back left, smaller */}
        <g transform="translate(178, 58) rotate(-8)">
          <rect
            x="0"
            y="0"
            width="110"
            height="36"
            rx="4"
            fill="#ede9f8"
            stroke="#c4baf0"
            strokeWidth="1"
            opacity="0.85"
          />
          <polyline points="0,0 55,22 110,0" fill="none" stroke="#c4baf0" strokeWidth="1" opacity="0.85" />
        </g>

        {/* Floating envelope 3 - back right, smaller */}
        <g transform="translate(400, 48) rotate(6)">
          <rect
            x="0"
            y="0"
            width="110"
            height="36"
            rx="4"
            fill="#ede9f8"
            stroke="#c4baf0"
            strokeWidth="1"
            opacity="0.75"
          />
          <polyline points="0,0 55,22 110,0" fill="none" stroke="#c4baf0" strokeWidth="1" opacity="0.75" />
        </g>

        {/* Down arrows indicating incoming */}
        <g opacity="0.55">
          <line x1="340" y1="24" x2="340" y2="42" stroke="#7c6fcd" strokeWidth="2.5" strokeLinecap="round" />
          <polyline
            points="333,36 340,44 347,36"
            fill="none"
            stroke="#7c6fcd"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </g>
        <g opacity="0.3">
          <line x1="310" y1="28" x2="310" y2="42" stroke="#7c6fcd" strokeWidth="2" strokeLinecap="round" />
          <polyline
            points="304,37 310,44 316,37"
            fill="none"
            stroke="#7c6fcd"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </g>
        <g opacity="0.3">
          <line x1="370" y1="28" x2="370" y2="42" stroke="#7c6fcd" strokeWidth="2" strokeLinecap="round" />
          <polyline
            points="364,37 370,44 376,37"
            fill="none"
            stroke="#7c6fcd"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </g>

        {/* Decorative dots */}
        <circle cx="162" cy="145" r="3.5" fill="#c4baf0" opacity="0.45" />
        <circle cx="150" cy="162" r="2" fill="#c4baf0" opacity="0.3" />
        <circle cx="520" cy="138" r="3.5" fill="#c4baf0" opacity="0.45" />
        <circle cx="534" cy="155" r="2" fill="#c4baf0" opacity="0.3" />

        {/* Sparkle */}
        <path
          d="M476 72 L477.5 77 L483 78.5 L477.5 80 L476 85 L474.5 80 L469 78.5 L474.5 77 Z"
          fill="#a99ee0"
          opacity="0.5"
        />
        <path d="M204 78 L205 82 L209 83 L205 84 L204 88 L203 84 L199 83 L203 82 Z" fill="#a99ee0" opacity="0.4" />
      </svg>

      {(title || subtitle) && (
        <div style={{ textAlign: "center", marginTop: 8 }}>
          {title && (
            <p style={{ margin: 0, fontSize: 15, color: "var(--color-text-secondary, #888)", fontWeight: 400 }}>
              {title}
            </p>
          )}
          {subtitle && (
            <p style={{ margin: "4px 0 0", fontSize: 13, color: "var(--color-text-tertiary, #aaa)", fontWeight: 400 }}>
              {subtitle}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default IncomingMails;
